// /playback/:deviceId — cloud recording playback (spec 17 §2/§3/§5).
//
// Data flow: date → local-day µs window → GET /v3/devices/{id}/recordings
// (items carry clip_id; this build returns DESCENDING clip_start_ts with NO
// cursor — verified live — so deeper pages are fetched by walking `to` down
// past the oldest loaded clip) → clip list + <video> on the signed url.
// URL expiry → GET /v3/recordings/{clip_id}/url refetched ONCE per attempt.
// Event markers come from GET /v3/devices/{id}/events over the same window.
//
// SD 卡機（recording.location === 'sdcard'；spec 17 §3.4「SD 卡片段」+ §5.3，業主 2026-09-09 裁決）：
// 同一頁改列「SD 卡」片段——`PlaybackQuery day-ranges`（continuous_only）切 ≤5 分鐘 chunk +
// `range-events` 事件片段（lib/recPlayback/sdClips），點播走既有 SD 連續窗播放（device VOD
// `/pb.m3u8?t1&t2` 經 WebRTC PB/1 tunnel + hls.js），「下載這段」逐段拉 TS 接續 → 同一條
// transmux 腿出 MP4。鏡像 Android `Playback.kt` / `SdcardPlaybackController.fetchWindowTs`。

import { camZone, dayWindowInZone, todayInZone } from '../../lib/camTz';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mediaFetchUrl } from '../../lib/recPlayback/cloudM3u8';
import { CameraTzNote } from '../../components/CameraTzNote';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../../lib/api';
import { clipUrlToMp4Blob, transmuxTsToMp4, withTimeout } from '../../lib/recPlayback/tsToMp4';
import { HlsPlayer, type HlsLoaderFactory, type HlsSource } from '../../components/recPlayback/HlsPlayer';
import { pbPlaylistUrl } from '../../lib/recPlayback/hlsSource';
import { openRemotePlayback, type RemotePlaybackSession } from '../../lib/recPlayback/webrtcPlayback';
import { makeDataChannelLoaderClass } from '../../lib/recPlayback/dataChannelLoader';
import { recFlag } from '../../lib/recPlayback/flags';
import { queryDayRanges, queryRangeEvents } from '../../lib/recPlayback/sdcardQuery';
import { fetchSdWindowTs, pbEventRanges, clipDownloadFileName, sdClipRows, sdEventWindow, type SdClip } from '../../lib/recPlayback/sdClips';
import { Illustration } from '../../components/Illustration';
import { CaptureControls } from '../capture/CaptureControls';
import { PipCastControls } from '../pip/PipCastControls';
import { formatDuration, formatTimestampUs } from '../../lib/format';
import { dateFromUsSafe, fetchAllEventsInWindow, loadFirstRecordings, loadMoreRecordings, toDateInputValue, type DeviceSummary, type EventItem } from './playbackLib';
import { eventNoRecording, forgetEventSnapshot, isPlaybackDetectionEvent, loadEventSnapshot } from '../events/eventsLib';
import { deviceName } from '../devices/deviceModel';
import { TimelineStrip } from './TimelineStrip';
import './playback.css';

interface RecordingClip {
  clip_id: string;
  clip_start_ts: number;
  clip_end_ts: number;
  has_event: boolean;
  quality?: string;
  url: string;
}

type Gate = 'sdcard' | 'forbidden' | null;
type LoadState = 'loading' | 'ready' | 'error';

const GAP_US = 2_000_000; // >2s between clips = no-recording gap (spec 17 §3.1)
const PLAYBACK_SPEEDS = [0.5, 1, 1.5, 2, 4] as const; // 倍速選項（APP#8，對齊 Vistaflow M5）

// 時刻以攝影機時區顯示（camTz；不知道 = 這台電腦的時區）
const timeOnly = (us: number, timeZone?: string) => formatTimestampUs(us, { year: undefined, month: undefined, day: undefined, timeZone });

function urlStillValid(url: string): boolean {
  try {
    const exp = new URL(url).searchParams.get('exp');
    if (!exp) return true;
    return Number(exp) * 1000 > Date.now() + 5000;
  } catch {
    return true;
  }
}

/** 以隱形 <a download> 觸發瀏覽器存檔（雲端 clip 與 SD 片段共用）。 */
function triggerDownload(href: string, name: string) {
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

type SdListState = 'loading' | 'ready' | 'unreachable';

// SD 片段列左側縫圖（spec 17 §3.4）：以時間對上 logbook 事件的 snapshot 縝圖——與事件時間軸列
// 同一條 loadEventSnapshot 快取腿（記憶體 → IndexedDB → 網路）；對不到事件 / 無圖 / 載入失敗
// → 中性影片 glyph 占位圖。onError 只清該 eid、不重抓（避免 404 閃爍迴圈，同 EventThumb）。
function SdClipThumb({ deviceId, ev }: { deviceId: string; ev: EventItem | undefined }) {
  const eid = ev?.eid;
  const hasSnapshot = Boolean(ev?.has_snapshot);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    setUrl(null);
    if (!eid || !hasSnapshot) return;
    const ctrl = new AbortController();
    let alive = true;
    void loadEventSnapshot(deviceId, eid, ctrl.signal).then((u) => {
      if (alive && u) setUrl(u);
    });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [deviceId, eid, hasSnapshot]);
  return (
    <span className="pb-clip-thumb" aria-hidden>
      {url ? (
        <img
          className="pb-clip-thumb-img"
          src={url}
          alt=""
          onError={() => {
            if (eid) forgetEventSnapshot(eid);
            setUrl(null);
          }}
        />
      ) : (
        <span className="pb-clip-thumb-glyph" />
      )}
    </span>
  );
}

export default function PlaybackPage() {
  const { t } = useTranslation();
  const { deviceId } = useParams<{ deviceId: string }>();
  const [searchParams] = useSearchParams();
  const seekTsUs = useMemo(() => {
    const t = Number(searchParams.get('t'));
    return Number.isFinite(t) && t > 0 ? t : null;
  }, [searchParams]);

  const [date, setDate] = useState(() => toDateInputValue(seekTsUs ? dateFromUsSafe(seekTsUs) : new Date()));
  // 攝影機時區：日期 = 攝影機當地那一天（對齊 Android）。開頁時可能還不知道時區 → 知道後、使用者還沒自己挑日期就重算預設日期
  const [devicesLoaded, setDevicesLoaded] = useState(0);
  const zone = useMemo(() => camZone(deviceId), [deviceId, devicesLoaded]);
  const dateTouched = useRef(false);
  useEffect(() => {
    if (!zone || dateTouched.current) return;
    setDate(seekTsUs ? todayInZone(zone, seekTsUs) : todayInZone(zone));
  }, [zone, seekTsUs]);
  const [device, setDevice] = useState<DeviceSummary | null>(null);
  const [clips, setClips] = useState<RecordingClip[]>([]);
  // spec 17 §2.2 lazy list model: `reachedStart` = the day's oldest range is loaded (no more
  // pages); `loadingMore` guards the scroll-bottom / scrubber-boundary "load older" trigger.
  const [reachedStart, setReachedStart] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [gate, setGate] = useState<Gate>(null);
  const [eventsInDay, setEventsInDay] = useState<EventItem[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  // player state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [currentIdx, setCurrentIdx] = useState<number | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [rate, setRate] = useState(1); // 倍速（APP#8）
  const [speedMenu, setSpeedMenu] = useState(false); // 影像右下角齒輪選單（業主 2026-09-08 選 A）
  const [downloading, setDownloading] = useState(false);
  // Global playhead in µs (current clip start + video offset) for the timeline strip.
  const [playheadUs, setPlayheadUs] = useState<number | null>(null);
  const playObjectUrlRef = useRef<string | null>(null);   // 雲端 TS 轉成的 MP4 blob（換片段時釋放）
  const pendingSeekSecRef = useRef<number | null>(null);
  const refreshedThisAttemptRef = useRef(false);
  const urlOverrides = useRef(new Map<string, string>());
  const deepLinkAppliedRef = useRef(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOwner = device?.role === 'owner';
  const win = useMemo(() => dayWindowInZone(date, zone), [date, zone]);

  // ---- SD 卡機狀態（spec 17 §3.4 SD 卡片段 / §5.3；業主 2026-09-09）----
  // gate 'sdcard' 不再是死路：同頁改列 SD 片段。雲端 clip 的 effect 仍以 gate !== null 略過。
  const isSd = gate === 'sdcard';
  const remoteEnabled = recFlag('REC_FEAT_PLAYBACK_REMOTE');
  const [sdRows, setSdRows] = useState<SdClip[]>([]);
  const [sdState, setSdState] = useState<SdListState>('loading');
  /** 正在播放/準備播放的 SD 窗（清單列或 ?t= 深連結的事件窗）。 */
  const [sdCurrent, setSdCurrent] = useState<SdClip | null>(null);
  const [sdSource, setSdSource] = useState<HlsSource | null>(null);
  const [sdLoader, setSdLoader] = useState<HlsLoaderFactory | undefined>(undefined);
  const [sdDownloadingId, setSdDownloadingId] = useState<string | null>(null);
  // 一條 WebRTC PB/1 tunnel session 供播放與下載共用（PbTunnel 內部序列化請求）；換機/卸載時關閉。
  // 以 promise 快取避免連點時開出兩條 session。
  const sdSessionRef = useRef<RemotePlaybackSession | null>(null);
  const sdSessionPromiseRef = useRef<Promise<RemotePlaybackSession> | null>(null);
  const sdPlaySeqRef = useRef(0);
  // HlsPlayer 自帶 <video>；截圖/錄影/PiP 與 ±5s 需要該元素 → 由外層 wrap 的 callback ref 撈出。
  const sdVideoRef = useRef<HTMLVideoElement | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  // 空日不再是死路(design review P0-2):查最近一筆雲端錄影 → 跳到該日。
  // recordings 為 clip_start_ts 降冪(本 build,§2.1),wide 視窗 limit=1 即最新。
  const [jumping, setJumping] = useState(false);
  const jumpToLatest = useCallback(async () => {
    if (!deviceId) return;
    setJumping(true);
    try {
      const nowUs = Date.now() * 1000;
      const res = await apiFetch<RecordingClip[]>(`/v3/devices/${encodeURIComponent(deviceId)}/recordings`, {
        query: { from: nowUs - 90 * 86_400_000_000, to: nowUs, limit: 1 },
      });
      const latest = (res.data ?? [])[0];
      if (latest) setDate(toDateInputValue(dateFromUsSafe(latest.clip_start_ts)));
      else showToast(t('playback.toastNoRecordingsInRetention'));
    } catch {
      showToast(t('playback.toastJumpLatestFailed'));
    } finally {
      setJumping(false);
    }
  }, [deviceId, showToast, t]);

  // ---- device info (role for upsell mask §5.2, recording.location for sdcard §5.3) ----
  useEffect(() => {
    if (!deviceId) return;
    let alive = true;
    apiFetch<DeviceSummary[]>('/v3/devices')
      .then((res) => {
        if (!alive) return;
        const d = (res.data ?? []).find((x) => x.device_id === deviceId) ?? null;
        setDevice(d);
        setDevicesLoaded((n) => n + 1); // apiFetch 已記下攝影機時區 → 重算 zone
        if (d?.recording?.location === 'sdcard') setGate('sdcard');
      })
      .catch(() => {
        /* best-effort; clips fetch is the authority for hard failures */
      });
    return () => {
      alive = false;
    };
  }, [deviceId, reloadKey]);

  // ---- clip list for the selected local day (spec 17 §2.2 lazy, cache-first) ----
  // Load ONLY the first (newest) range on entry — no eager whole-day drain. Older ranges are
  // pulled on demand by loadMore() (scroll bottom / scrubber boundary). loadFirstRecordings is
  // cache-first: re-opening a day this session repaints from memory with no network.
  useEffect(() => {
    if (!deviceId || gate !== null) return;
    let alive = true;
    setLoadState('loading');
    setLoadError(null);
    setClips([]);
    setReachedStart(false);
    setLoadingMore(false);
    setCurrentIdx(null);
    setSrc(null);
    urlOverrides.current.clear();

    loadFirstRecordings(deviceId, win)
      .then((res) => {
        if (!alive) return;
        setClips(res.clips);
        setReachedStart(res.reachedStart);
        setLoadState('ready');
      })
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 403) {
          // ADR-024: 撤權 / permission changed — 下次 fetch 生效 (spec 17 §3.3 point 2)
          setGate('forbidden');
        } else {
          // 回放不受訂閱閘 (spec 17 §3.3 point 1;後端 media.go 只驗裝置關係)。
          // 422 不再視為「訂閱失效」——落一般錯誤,不擋播/不踢訂閱頁。
          setLoadState('error');
          setLoadError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.networkErrorRetry'));
        }
      });
    return () => {
      alive = false;
    };
  }, [deviceId, win.fromUs, win.toUs, gate, reloadKey, t]);

  // Pull the next older range (spec 17 §2.2). Idempotent while a page is in flight or the day
  // start is reached; the playbackLib pager dedupes concurrent calls and serves from cache.
  const loadingMoreRef = useRef(false);
  const loadMore = useCallback(async (): Promise<RecordingClip[] | null> => {
    if (!deviceId || gate !== null || reachedStart || loadingMoreRef.current) return null;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await loadMoreRecordings(deviceId, win);
      setClips(res.clips);
      setReachedStart(res.reachedStart);
      return res.clips;
    } catch {
      // transient — the trigger fires again on the next scroll/scrub
      return null;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [deviceId, gate, reachedStart, win.fromUs, win.toUs]);

  // Scroll-bottom trigger (spec 17 §2.2): observe a sentinel at the end of the (newest→oldest)
  // clip list; when it scrolls into view and older ranges remain, pull the next earlier range.
  const bottomSentinelRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const el = bottomSentinelRef.current;
    if (!el || loadState !== 'ready' || reachedStart) return;
    if (typeof IntersectionObserver === 'undefined') return; // jsdom / SSR: no scroll trigger
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore();
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadState, reachedStart, loadMore, clips.length]);

  // ---- event markers for the same window (best-effort; spec 17 §4.3) ----
  // SD 卡機也要抓：SD 片段列的縫圖以時間對上 logbook 事件的 snapshot（spec 17 §3.4）。
  useEffect(() => {
    if (!deviceId || gate === 'forbidden') return;
    let alive = true;
    setEventsInDay([]);
    fetchAllEventsInWindow(deviceId, win.fromUs, win.toUs)
      .then((evs) => {
        // spec 17 §4.1：scrubber marker 與「含事件 ×N」計數只收攝影機偵測 cluster（排除 0x80C0 等操作/
        // 系統事件）+ 排除無錄影事件（record_mode==none）。
        if (alive) setEventsInDay(evs.filter((e) => isPlaybackDetectionEvent(e) && !eventNoRecording(e)));
      })
      .catch(() => {
        /* markers are decorative — list still works without them */
      });
    return () => {
      alive = false;
    };
  }, [deviceId, win.fromUs, win.toUs, gate, reloadKey]);

  const eventCountByClip = useMemo(() => {
    const m = new Map<string, number>();
    if (clips.length === 0 || eventsInDay.length === 0) return m;
    for (const ev of eventsInDay) {
      const clip = clips.find((c) => c.clip_start_ts <= ev.event_ts && ev.event_ts <= c.clip_end_ts);
      if (clip) m.set(clip.clip_id, (m.get(clip.clip_id) ?? 0) + 1);
    }
    return m;
  }, [clips, eventsInDay]);

  // ---- url refresh (GET /v3/recordings/{clip_id}/url, spec 17 §3.3) ----
  const refreshClipUrl = useCallback(
    async (clip: RecordingClip): Promise<string | null> => {
      // orphan index rows can carry clip_id:"" — refreshing would hit
      // GET /v3/recordings//url (301→404); just fail the attempt locally
      if (!clip.clip_id) return null;
      try {
        const res = await apiFetch<{ url: string }>(`/v3/recordings/${encodeURIComponent(clip.clip_id)}/url`);
        const url = res.data?.url;
        if (!url) return null;
        urlOverrides.current.set(clip.clip_id, url);
        return url;
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) setGate('forbidden');
        // 422 不視為訂閱失效(回放不受訂閱閘,spec 17 §3.3);換發失敗即回 null。
        return null;
      }
    },
    []
  );

  const playClip = useCallback(
    // `list` defaults to the current `clips` state, but the scrubber-boundary path passes the
    // freshly-grown list it resolved `idx` against: after loadMore() the `.then` runs as a
    // microtask BEFORE React re-renders, so this closure's `clips` is still the pre-load array
    // (older clips get prepended → every index shifts). Looking `clip` up in `list[idx]` keeps
    // idx and clip consistent; `idx` is the eventual index into the committed `clips` state too.
    async (idx: number, seekUs?: number, list: RecordingClip[] = clips) => {
      const clip = list[idx];
      if (!clip) return;
      setCurrentIdx(idx);
      refreshedThisAttemptRef.current = false;
      pendingSeekSecRef.current = seekUs !== undefined ? Math.max(0, (seekUs - clip.clip_start_ts) / 1_000_000) : null;
      let url = urlOverrides.current.get(clip.clip_id) ?? clip.url;
      if (!urlStillValid(url)) {
        // pre-emptive refresh: signed url already past `exp`
        refreshedThisAttemptRef.current = true;
        const fresh = await refreshClipUrl(clip);
        if (!fresh) {
          showToast(t('playback.toastUrlFetchFailed'));
          return;
        }
        url = fresh;
      }
      // ls-loop（MW）：api 的公開網址是 http://<區網 IP>:8081，網頁是 https 同網域 → 換成同網域路徑，免得被擋（mixed content）
      const media = mediaFetchUrl(url);
      // 雲端片段是 MPEG-TS（spec 17 §4.3）：Chrome 的 <video> 不能直接播 TS → 先轉成 MP4（同下載的 mux.js 路徑）再播；
      // 轉失敗才退回原網址（Safari 也許播得動）。一段約 15 s，轉換很快。
      if (/\.ts(?:$|[?&#])|%2F[^&]*\.ts(?:$|&)|key=[^&]*\.ts(?:$|&)/i.test(media)) {
        try {
          const blob = await withTimeout(clipUrlToMp4Blob(media, () => refreshClipUrl(clip)), 10_000);
          if (playObjectUrlRef.current) URL.revokeObjectURL(playObjectUrlRef.current);
          playObjectUrlRef.current = URL.createObjectURL(blob);
          setSrc(playObjectUrlRef.current);
          return;
        } catch (e) {
          console.warn('[playClip] TS → MP4 failed, falling back to the url', e);
        }
      }
      setSrc(media);
    },
    [clips, refreshClipUrl, showToast, t]
  );

  // ---- download / save this clip (spec 17 §3.4) ----
  // The cloud clip on S3 is an MPEG-TS container (spec 17 §4.3). Saving the raw bytes
  // as ".mp4" is a mislabeled/fake mp4 that strict players/apps reject — so fetch the
  // bytes (via the same-origin media proxy, like playback) and transmux TS → real MP4
  // (mux.js) before saving, mirroring the iOS/Android download path. Re-signs once on
  // expiry via refreshClipUrl.
  const downloadClip = useCallback(
    async (clip: RecordingClip) => {
      if (!clip.clip_id || downloading) return;
      setDownloading(true);
      let objectUrl: string | null = null;
      try {
        // always re-sign: a stale override could already be expired
        const fresh = await refreshClipUrl(clip);
        const url = fresh ?? urlOverrides.current.get(clip.clip_id) ?? clip.url;
        if (!url) {
          showToast(t('playback.toastDownloadUrlFailed'));
          return;
        }
        const name = clipDownloadFileName(deviceId ?? '', clip.clip_start_ts);
        try {
          // fetch bytes + transmux TS → real MP4. Bounded so the button can never stick;
          // on any failure fall back to a direct download of the signed url (raw bytes) so
          // the user always gets a file rather than a hung/greyed button.
          const blob = await withTimeout(clipUrlToMp4Blob(url, () => refreshClipUrl(clip)), 10_000);
          objectUrl = URL.createObjectURL(blob);
          triggerDownload(objectUrl, name);
        } catch (e) {
          console.error('[downloadClip] transmux failed, falling back to direct link', e);
          triggerDownload(url, name);
        }
        showToast(t('playback.toastDownloadStarted'));
      } catch {
        showToast(t('playback.toastDownloadUrlFailed'));
      } finally {
        if (objectUrl) {
          const u = objectUrl;
          setTimeout(() => URL.revokeObjectURL(u), 30_000);
        }
        setDownloading(false);
      }
    },
    [deviceId, downloading, refreshClipUrl, showToast, t]
  );

  // ---- SD 卡片段清單（spec 17 §3.4「SD 卡片段」；業主 2026-09-09；鏡像 Android sdClipRows）----
  // 資料源 = day-ranges(continuous_only)切 ≤5 分鐘 chunk + range-events 事件片段。兩個查詢都騎
  // signaling socket：硬重載後 socket 可能還沒連上 → UNREACHABLE，短暫重試幾次（同 EventTimelineView）。
  useEffect(() => {
    if (!deviceId || !isSd) return;
    let alive = true;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setSdState('loading');
    setSdRows([]);
    setSdCurrent(null);
    setSdSource(null);
    setPlayheadUs(null);
    const run = async () => {
      const [ranges, events] = await Promise.all([
        queryDayRanges(deviceId, win.fromUs, win.toUs, true),
        queryRangeEvents(deviceId, win.fromUs, win.toUs),
      ]);
      if (!alive) return;
      const unreachable = ranges.state === 'UNREACHABLE' && events.state === 'UNREACHABLE';
      if (unreachable && attempt < 4) {
        attempt += 1;
        timer = setTimeout(() => void run(), 1500);
        return;
      }
      setSdRows(sdClipRows(ranges.items, pbEventRanges(events.items)));
      setSdState(unreachable ? 'unreachable' : 'ready');
    };
    void run();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [deviceId, isSd, win.fromUs, win.toUs, reloadKey]);

  // 換機 / 卸載：關閉 tunnel session（stop_playback + 關 PC）。
  useEffect(
    () => () => {
      sdSessionRef.current?.stop();
      sdSessionRef.current = null;
      sdSessionPromiseRef.current = null;
    },
    [deviceId]
  );

  const dropSdSession = useCallback(() => {
    sdSessionRef.current?.stop();
    sdSessionRef.current = null;
    sdSessionPromiseRef.current = null;
  }, []);

  // 開（或重用）到本機的 WebRTC data-channel session；播放與下載共用同一條。
  const ensureSdSession = useCallback((): Promise<RemotePlaybackSession> => {
    if (!deviceId) return Promise.reject(new Error('no device'));
    if (!sdSessionPromiseRef.current) {
      sdSessionPromiseRef.current = openRemotePlayback(deviceId).then(
        (s) => {
          sdSessionRef.current = s;
          return s;
        },
        (e: unknown) => {
          sdSessionPromiseRef.current = null;
          throw e;
        }
      );
    }
    return sdSessionPromiseRef.current;
  }, [deviceId]);

  // 點播 SD 窗：直接播 [start,end]（與 ContinuousTimelineView / SD 事件點播同一條 device VOD 腿），
  // **不**經「找包含它的連續區段」——事件錄影機沒有連續區段，那樣會找不到。
  const playSdClip = useCallback(
    async (clip: SdClip) => {
      if (!remoteEnabled) {
        showToast(t('recPlayback.sdcardLanOnly'));
        return;
      }
      const seq = ++sdPlaySeqRef.current;
      setSdCurrent(clip);
      setSdSource(null);
      setPlayheadUs(clip.startUs);
      try {
        const session = await ensureSdSession();
        if (seq !== sdPlaySeqRef.current) return; // 被更新的點擊取代
        const tunnel = session.tunnel;
        // /seg 404 = 該段已被環形覆蓋（每次點播只提示一次，避免逐段 404 洗 toast）。
        let warned = false;
        const lf: HlsLoaderFactory = () =>
          makeDataChannelLoaderClass({
            tunnel,
            onRecycle404: () => {
              if (warned) return;
              warned = true;
              showToast(t('playback.sdOverwritten'));
            },
          });
        setSdLoader(() => lf);
        setSdSource({ kind: 'device-vod', m3u8Url: pbPlaylistUrl('', clip.startUs, clip.endUs) });
      } catch {
        if (seq !== sdPlaySeqRef.current) return;
        // VOD session 建不起來 = 裝置離線（spec 17 §5.3 三態之一）。
        showToast(t('playback.sdOffline'));
      }
    },
    [remoteEnabled, ensureSdSession, showToast, t]
  );

  // 同一連續區段的相鄰 chunk 接著播（chunk 邊界正好相接）；其餘停在片尾。
  const onSdEnded = useCallback(() => {
    if (!sdCurrent) return;
    const next = sdRows.find((r) => r.startUs === sdCurrent.endUs && !r.hasEvent);
    if (next) void playSdClip(next);
  }, [sdCurrent, sdRows, playSdClip]);

  const onSdError = useCallback(
    (fatal: boolean) => {
      if (!fatal) return;
      showToast(t('recPlayback.playFailed'));
      // 致命錯誤多半是 data channel 已斷；丟掉 session，下次點播重新建立。
      dropSdSession();
      setSdSource(null);
    },
    [showToast, t, dropSdSession]
  );

  // SD「下載這段」（spec 17 §3.4 SD 腿；鏡像 Android downloadSdWindow）：/pb.m3u8 → 逐段拉 TS 接續
  // → 同 downloadClip 的 transmux（mux.js）→ blob 存檔。整段無片（全 404）→「已被覆蓋」提示、不算失敗。
  // 不套 withTimeout：5 分鐘 chunk 逐段經 tunnel 拉可能要數分鐘；PbTunnel 自有 30s 單請求逾時兜底。
  const downloadSdClip = useCallback(
    async (clip: SdClip) => {
      if (!deviceId || downloading) return;
      if (!remoteEnabled) {
        showToast(t('recPlayback.sdcardLanOnly'));
        return;
      }
      setDownloading(true);
      setSdDownloadingId(clip.id);
      let objectUrl: string | null = null;
      try {
        const session = await ensureSdSession();
        const { ts, written } = await fetchSdWindowTs((u) => session.tunnel.requestUrl(u), clip.startUs, clip.endUs);
        if (written === 0) {
          showToast(t('playback.sdOverwritten'));
          return;
        }
        const blob = await transmuxTsToMp4(ts);
        objectUrl = URL.createObjectURL(blob);
        triggerDownload(objectUrl, clipDownloadFileName(deviceId, clip.startUs));
        showToast(t('playback.toastDownloadStarted'));
      } catch (e) {
        console.error('[downloadSdClip] failed', e);
        showToast(t('common.downloadFailedRetry'));
      } finally {
        if (objectUrl) {
          const u = objectUrl;
          setTimeout(() => URL.revokeObjectURL(u), 30_000);
        }
        setSdDownloadingId(null);
        setDownloading(false);
      }
    },
    [deviceId, downloading, remoteEnabled, ensureSdSession, showToast, t]
  );

  // SD 片段列縫圖：每列對上「event_ts 落在 [start,end] 且最接近 start」的 logbook 事件；
  // 有 snapshot 的優先（沒圖的對上也只會是占位圖）。
  const sdThumbEvent = useMemo(() => {
    const m = new Map<string, EventItem>();
    if (!isSd || sdRows.length === 0 || eventsInDay.length === 0) return m;
    for (const row of sdRows) {
      let best: EventItem | undefined;
      for (const ev of eventsInDay) {
        if (ev.event_ts < row.startUs || ev.event_ts > row.endUs) continue;
        if (!best) {
          best = ev;
          continue;
        }
        const evHas = Boolean(ev.has_snapshot && ev.eid);
        const bestHas = Boolean(best.has_snapshot && best.eid);
        if (evHas !== bestHas ? evHas : ev.event_ts < best.event_ts) best = ev;
      }
      if (best) m.set(row.id, best);
    }
    return m;
  }, [isSd, sdRows, eventsInDay]);

  // ---- deep-link seek from ?t= (event → clip, spec 17 §4.3/§4.4) ----
  useEffect(() => {
    deepLinkAppliedRef.current = false;
  }, [seekTsUs, deviceId]);
  // SD 卡機的 ?t=（事件列點入）：播該事件的 −5s…+10s SD 片段（spec 17 §5.3）——有對應事件列就播那列
  // （同窗），否則直接播事件窗；不走 5 分鐘 chunk（事件可能在 chunk 第 4 分鐘，還得手動 seek）。
  useEffect(() => {
    if (!isSd || sdState !== 'ready' || seekTsUs === null || deepLinkAppliedRef.current) return;
    deepLinkAppliedRef.current = true;
    const hit = sdRows.find((r) => r.hasEvent && r.startUs <= seekTsUs && seekTsUs <= r.endUs);
    void playSdClip(hit ?? sdEventWindow(seekTsUs));
  }, [isSd, sdState, seekTsUs, sdRows, playSdClip]);
  useEffect(() => {
    if (isSd || loadState !== 'ready' || seekTsUs === null || deepLinkAppliedRef.current || clips.length === 0) return;
    deepLinkAppliedRef.current = true;
    const hitIdx = clips.findIndex((c) => c.clip_start_ts <= seekTsUs && seekTsUs <= c.clip_end_ts);
    if (hitIdx >= 0) {
      void playClip(hitIdx, seekTsUs);
      return;
    }
    const nextIdx = clips.findIndex((c) => c.clip_start_ts >= seekTsUs);
    if (nextIdx >= 0) {
      showToast(t('playback.toastSeekToNearest'));
      void playClip(nextIdx);
    } else {
      showToast(t('playback.toastNoRecordingAfter'));
    }
  }, [isSd, loadState, seekTsUs, clips, playClip, showToast, t]);

  // ---- video element handlers ----
  // ±5 秒（業主 2026-09-08；spec 17 §3.7a）：對目前 <video> 相對 seek，clamp 在 [0, duration]。
  // SD 卡機的 <video> 在 HlsPlayer 內（sdVideoRef 由 wrap 撈出）。
  const skipBy = useCallback((sec: number) => {
    const v = videoRef.current ?? sdVideoRef.current;
    if (!v) return;
    const max = Number.isFinite(v.duration) ? v.duration : Infinity;
    v.currentTime = Math.max(0, Math.min(max, v.currentTime + sec));
  }, []);

  const onLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    // 換片段時瀏覽器把 playbackRate 重置回 1 → 在 metadata 就緒後重套選定倍速（APP#8）
    v.playbackRate = rate;
    const seek = pendingSeekSecRef.current;
    if (seek !== null && seek > 0) v.currentTime = seek;
    pendingSeekSecRef.current = null;
    v.play().catch(() => {
      /* autoplay policy — user can press play */
    });
  }, [rate]);

  // 倍速即時生效（播放中切速）；換 src 由 onLoadedMetadata 重套（APP#8）
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = rate;
  }, [rate]);

  const onVideoError = useCallback(() => {
    if (currentIdx === null) return;
    const clip = clips[currentIdx];
    if (!clip) return;
    if (!refreshedThisAttemptRef.current) {
      // most likely S3 AccessDenied on an expired url → refetch ONCE and resume in place
      refreshedThisAttemptRef.current = true;
      const v = videoRef.current;
      if (v && v.currentTime > 0) pendingSeekSecRef.current = v.currentTime;
      void refreshClipUrl(clip).then((url) => {
        if (url) setSrc(url);
        else showToast(t('playback.toastClipUnplayable'));
      });
    } else {
      showToast(t('playback.toastClipUnplayable'));
    }
  }, [currentIdx, clips, refreshClipUrl, showToast, t]);

  const onEnded = useCallback(() => {
    if (currentIdx === null) return;
    const cur = clips[currentIdx];
    const next = clips[currentIdx + 1];
    if (!next || !cur) {
      showToast(t('playback.toastReachedLast'));
      return;
    }
    if (next.clip_start_ts - cur.clip_end_ts > GAP_US) showToast(t('playback.toastGapSkippedNext'));
    void playClip(currentIdx + 1);
  }, [currentIdx, clips, playClip, showToast, t]);

  // ---- timeline playhead: video offset → global µs (spec 17 §3.1) ----
  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v || currentIdx === null) return;
    const clip = clips[currentIdx];
    if (clip) setPlayheadUs(clip.clip_start_ts + Math.round(v.currentTime * 1_000_000));
  }, [currentIdx, clips]);

  // ---- timeline seek: µs → clip (containing, else nearest forward, spec 17 §4.3) ----
  // Resolve a seek against a given clip list (containing clip → play at offset; else the
  // nearest forward clip). Kept list-parametric so a scrubber-boundary loadMore can retry
  // against the freshly grown list without waiting for a React re-render.
  const resolveSeek = useCallback(
    (list: RecordingClip[], us: number) => {
      const hit = list.findIndex((c) => c.clip_start_ts <= us && us <= c.clip_end_ts);
      if (hit >= 0) {
        void playClip(hit, us, list);
        return;
      }
      const next = list.findIndex((c) => c.clip_start_ts >= us);
      if (next >= 0) {
        showToast(t('playback.toastSeekToNearest'));
        void playClip(next, undefined, list);
      } else {
        showToast(t('playback.toastNoRecordingAfter'));
      }
    },
    [playClip, showToast, t]
  );
  const seekToUs = useCallback(
    (us: number) => {
      if (clips.length === 0) return;
      setPlayheadUs(us);
      // Scrubber pulled before the oldest-loaded clip and older ranges remain → pull the next
      // earlier range (spec 17 §2.2 scrubber-boundary trigger), then resolve against the grown list.
      if (!reachedStart && us < clips[0].clip_start_ts) {
        showToast(t('playback.loadingClips'));
        // A5 review #3: a deep jump may be older than a single page — keep paging until the
        // target is covered (or start reached / no progress / cap), not one page per gesture.
        void (async () => {
          let list = clips;
          for (let i = 0; i < 20; i++) {
            if (list.length && us >= list[0].clip_start_ts) break;
            const prevOldest = list.length ? list[0].clip_start_ts : Number.POSITIVE_INFINITY;
            const grown = await loadMore();
            if (!grown) break; // reachedStart / gate / inflight
            list = grown;
            if (list.length && list[0].clip_start_ts >= prevOldest) break; // no progress → stop
          }
          resolveSeek(list, us);
        })();
        return;
      }
      resolveSeek(clips, us);
    },
    [clips, reachedStart, loadMore, resolveSeek, showToast, t]
  );

  // ---- guards / gates ----

  if (!deviceId) {
    return (
      <section className="card" style={{ maxWidth: 560 }}>
        <p className="error-text" style={{ margin: 0 }}>
          {t('playback.missingDeviceIdPrefix')} <Link to="/devices">{t('playback.devicesLink')}</Link> {t('playback.missingDeviceIdSuffix')}
        </p>
      </section>
    );
  }

  // 〔已移除〕舊 gate === 'sdcard' 的「雲端回放不適用」死路卡：業主 2026-09-09 裁決 SD 卡機同頁改列
  // 「SD 卡」片段（spec 17 §3.4），見下方 isSd 分支；owner 切回雲端的 CTA 移到 SD 空狀態。

  if (gate === 'forbidden') {
    return (
      <section className="card pb-gate" role="alert">
        <h1 className="pb-gate-title">{t('playback.gateForbiddenTitle')}</h1>
        <p className="text-secondary">{t('playback.gateForbiddenBody')}</p>
        <Link className="btn-ghost pb-gate-cta" to="/devices">
          {t('playback.gateForbiddenCta')}
        </Link>
      </section>
    );
  }

  // 〔已移除〕舊 gate === 'subscription' 分支:回放不受訂閱閘(spec 17 §3.3),
  // 該 gate 已無觸發點(has_event 422 誤判移除後成死碼)。

  // ---- main view ----

  const currentClip = currentIdx !== null ? clips[currentIdx] : undefined;

  // rows with gap dividers (spec 17 §3.1 縫隙). Rendered NEWEST→OLDEST (spec 17 §2.2): the
  // newest range sits at the top, and the bottom = the oldest-loaded clip, so scrolling to
  // the bottom sentinel pulls the next earlier range. `idx` stays the ascending-array index
  // so player/seek math (playClip, onEnded currentIdx+1) is unchanged.
  const rows: ({ kind: 'clip'; idx: number } | { kind: 'gap'; sec: number; key: string })[] = [];
  for (let i = clips.length - 1; i >= 0; i--) {
    rows.push({ kind: 'clip', idx: i });
    if (i > 0) {
      const older = clips[i - 1];
      const gapUs = clips[i].clip_start_ts - older.clip_end_ts;
      if (gapUs > GAP_US) rows.push({ kind: 'gap', sec: gapUs / 1_000_000, key: `gap-${older.clip_id}` });
    }
  }

  // 疊在影像上只留 ±5 秒（CAM-26：倍速齒輪移出、改到影片下方薄工具列）。
  // 滑鼠移入顯示、觸控裝置常駐；播放暫停沿用瀏覽器原生列。
  const overlayControls = (
    <div className="pb-overlay">
      <button type="button" className="pb-skip" data-testid="pb-skip-back" aria-label={t('playback.skipBack5')} title={t('playback.skipBack5')} onClick={() => skipBy(-5)}>
        −5s
      </button>
      <button type="button" className="pb-skip" data-testid="pb-skip-forward" aria-label={t('playback.skipForward5')} title={t('playback.skipForward5')} onClick={() => skipBy(5)}>
        +5s
      </button>
    </div>
  );

  // CAM-26：倍速改成影片下方薄工具列的一顆按鈕（點開仍是五檔、打勾目前檔），與下載/截圖同列。
  const speedControl = (
    <div className="pb-speed-inline">
      <button
        type="button"
        className="btn-ghost pb-speed-btn"
        data-testid="pb-speed-gear"
        aria-label={t('playback.speedLabel')}
        aria-haspopup="menu"
        aria-expanded={speedMenu}
        onClick={() => setSpeedMenu((o) => !o)}
      >
        {t('playback.speedLabel')} {rate === 1 ? '' : <span className="pb-gear-rate">{rate}×</span>}
      </button>
      {speedMenu && (
        <ul className="pb-speed-menu" role="menu" data-testid="pb-speed">
          {PLAYBACK_SPEEDS.map((s) => (
            <li key={s} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={rate === s}
                data-testid={`pb-speed-${s}`}
                onClick={() => {
                  setRate(s);
                  setSpeedMenu(false);
                }}
              >
                {rate === s ? '✓ ' : ''}
                {s}×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // now-playing 列的共同資料：雲端 = 目前 clip；SD = 目前 SD 窗（spec 17 §3.4 兩種來源同一列 UI）。
  const nowPlaying = isSd
    ? sdCurrent && { startUs: sdCurrent.startUs, endUs: sdCurrent.endUs, meta: sdCurrent.hasEvent ? t('playback.sdClipHasEvent') : '', ready: !!sdSource }
    : currentClip && { startUs: currentClip.clip_start_ts, endUs: currentClip.clip_end_ts, meta: currentClip.quality ?? '', ready: !!src };
  const activeVideoRef = isSd ? sdVideoRef : videoRef;
  const onDownloadNow = () => {
    if (isSd) {
      if (sdCurrent) void downloadSdClip(sdCurrent);
    } else if (currentClip) {
      void downloadClip(currentClip);
    }
  };

  // SD 片段列最新在上（同雲端清單；sdClipRows 本身升冪）。
  const sdRowsNewestFirst = isSd ? [...sdRows].reverse() : [];

  return (
    <section className="pb-page">
      <header className="pb-header">
        <h1 style={{ margin: 0 }}>{t('playback.headerTitle', { name: device ? deviceName(device) : deviceId })}</h1>
        {device && <CameraTzNote tz={device.tz ?? null} />}
        {/* SD 卡機的事件走 SD 事件時間軸（雲事件時間軸依 record_location 濾掉 sdcard 列） */}
        <Link to={isSd ? `/rec-timeline/${encodeURIComponent(deviceId)}` : `/events/${encodeURIComponent(deviceId)}`}>{t('playback.eventTimelineLink')}</Link>
        {/* 多機同步回放（APP#43）：帶本機預選進共用時鐘多機頁；多機頁只吃雲端 clip，SD 卡機不給入口 */}
        {!isSd && <Link to={`/playback/multi?ids=${encodeURIComponent(deviceId)}`}>{t('playback.multiCamLink')}</Link>}
      </header>

      <div className="pb-toolbar">
        <input type="date" value={date} onChange={(e) => { dateTouched.current = true; setDate(e.target.value); }} aria-label={t('playback.datePickerLabel')} className="pb-date" />
        {/* 「只看有事件」開關已移除:雲端 clip 皆事件錨點,篩選 no-op 且會送 PARKED 的 has_event → 422（spec 17 §2.1／AC-PB-2） */}
        {/* 分享連結 blocked on GAP-PB-7（presigned URL 不得外傳充當分享連結，spec 17 §3.5）；SD 片段分享亦不在內（§3.4） */}
        <button className="btn-ghost pb-share" disabled title={t('playback.shareLinkComingTitle')}>
          {t('playback.shareLinkComing')}
        </button>
      </div>

      {toast && (
        <p className="pb-toast" role="status">
          {toast}
        </p>
      )}

      <div className="pb-player card">
        {isSd ? (
          sdSource && sdCurrent ? (
            // SD 窗播放：device VOD /pb.m3u8 經 PB/1 tunnel + hls.js（與 ContinuousTimelineView 同腿）。
            // wrap 的 callback ref 撈出 HlsPlayer 內的 <video> 供 ±5s / 截圖 / PiP 使用。
            <div
              className="pb-video-wrap"
              ref={(el) => {
                sdVideoRef.current = el ? el.querySelector('video') : null;
              }}
            >
              <HlsPlayer
                source={sdSource}
                loaderFactory={sdLoader}
                playbackRate={rate}
                className="pb-video"
                onEnded={onSdEnded}
                onError={onSdError}
                onTimeUpdate={(sec) => setPlayheadUs(sdCurrent.startUs + Math.round(sec * 1_000_000))}
              />
              {overlayControls}
            </div>
          ) : (
            <div className="pb-video pb-video-empty text-secondary">
              {sdCurrent ? t('recPlayback.loading') : sdRows.length > 0 ? t('playback.selectClipToPlay') : t('playback.noPlayableClip')}
            </div>
          )
        ) : src ? (
          <div className="pb-video-wrap">
            <video
              ref={videoRef}
              className="pb-video"
              src={src}
              controls
              playsInline
              onLoadedMetadata={onLoadedMetadata}
              onError={onVideoError}
              onEnded={onEnded}
              onTimeUpdate={onTimeUpdate}
            />
            {overlayControls}
          </div>
        ) : (
          <div className="pb-video pb-video-empty text-secondary">{clips.length > 0 ? t('playback.selectClipToPlay') : t('playback.noPlayableClip')}</div>
        )}
        {nowPlaying && (
          <div className="pb-nowplaying-row">
            <p className="text-secondary pb-nowplaying">
              {t('playback.nowPlaying', { start: timeOnly(nowPlaying.startUs, zone), end: timeOnly(nowPlaying.endUs, zone) })}
              {nowPlaying.meta ? ` · ${nowPlaying.meta}` : ''}
            </p>
            {/* CAM-26 薄工具列：倍速（移出齒輪）+ 下載 + 截圖，同一列直接露出 */}
            {speedControl}
            {/* 下載這段（spec 17 §3.4）：雲端 = 重簽短效 URL → transmux → 存檔；SD = tunnel 逐段拉 TS → 同一條 transmux 腿 */}
            <button className="btn-ghost pb-download" disabled={downloading} onClick={onDownloadNow}>
              {downloading ? t('playback.preparingDownload') : t('playback.downloadClip')}
            </button>
            {/* 本機截圖 / 錄影（APP#17）：截目前畫面 / 用 captureStream() 錄當前播放 */}
            <CaptureControls
              videoRef={activeVideoRef}
              mediaStream={null}
              deviceName={device ? deviceName(device) : deviceId}
              deviceId={deviceId}
              ready={nowPlaying.ready}
            />
            {/* 懸浮窗 / 投屏（APP#18）：PiP 邊滑邊看 + Remote Playback 投到大螢幕 */}
            <PipCastControls videoRef={activeVideoRef} ready={nowPlaying.ready} />
          </div>
        )}
      </div>

      {/* ===== SD 卡機：「SD 卡」片段清單（spec 17 §3.4；業主 2026-09-09；鏡像 Android Playback.kt SD 分頁） ===== */}
      {isSd && sdState === 'loading' && <p className="text-secondary">{t('playback.loadingClips')}</p>}

      {isSd && sdState === 'unreachable' && (
        <div className="card" role="alert" style={{ maxWidth: 560 }}>
          {/* VOD/PlaybackQuery 都問不到裝置 → §5.3「攝影機離線，無法讀取 SD 卡片段」 */}
          <p className="error-text" style={{ marginTop: 0 }}>{t('playback.sdOffline')}</p>
          <button className="btn-ghost" onClick={() => setReloadKey((k) => k + 1)}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {isSd && sdState === 'ready' && sdRows.length === 0 && (
        <div className="card" style={{ maxWidth: 560, textAlign: 'center' }}>
          <Illustration name="no-recordings" alt="" size={160} />
          <p style={{ marginTop: 0 }}>{t('playback.sdEmptyDay')}</p>
          <p className="text-secondary" style={{ marginBottom: isOwner ? 'var(--spacing-4)' : 0 }}>
            {t('playback.emptyTryAnotherDate')}
          </p>
          {/* owner 可切回雲端錄影（ADR-046 經裝置設定頁 op:set，入口在裝置設定頁） */}
          {isOwner && (
            <Link className="btn-ghost" to={`/devices/${encodeURIComponent(deviceId)}`}>
              {t('playback.gateSdcardCta')}
            </Link>
          )}
        </div>
      )}

      {isSd && sdState === 'ready' && sdRows.length > 0 && (
        <ul className="pb-cliplist" aria-label={t('playback.sdClipListLabel')} data-testid="pb-sd-cliplist">
          {sdRowsNewestFirst.map((clip) => {
            const active = sdCurrent?.id === clip.id;
            return (
              <li key={clip.id} className="pb-sdrow">
                {/* 播放鈕 = 整列；左側縫圖對上 logbook 事件 snapshot（對不到 → 占位圖） */}
                <button className={`pb-clip ${active ? 'active' : ''}`} onClick={() => void playSdClip(clip)}>
                  <SdClipThumb deviceId={deviceId} ev={sdThumbEvent.get(clip.id)} />
                  <span className="pb-clip-time">
                    {timeOnly(clip.startUs, zone)} – {timeOnly(clip.endUs, zone)}
                  </span>
                  <span className="pb-clip-meta text-secondary">{formatDuration((clip.endUs - clip.startUs) / 1_000_000)}</span>
                  <span className="pb-clip-badges">
                    {clip.hasEvent && <span className="pb-badge pb-badge-event">{t('playback.sdClipHasEvent')}</span>}
                    {active && <span className="pb-badge pb-badge-playing">{t('playback.playingBadge')}</span>}
                  </span>
                </button>
                {/* 儲存到手機（§3.4 SD 腿）：每列一顆，下載中顯示轉圈、其餘列鎖住（一次一筆，同 Android） */}
                <button
                  type="button"
                  className="btn-ghost pb-clip-dl"
                  disabled={downloading}
                  onClick={() => void downloadSdClip(clip)}
                  aria-label={t('playback.downloadClip')}
                  title={t('playback.downloadClip')}
                >
                  {sdDownloadingId === clip.id ? <span className="pb-spinner" aria-hidden /> : '⬇'}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* ===== 雲端機：既有雲端 clip 清單 ===== */}
      {/* 24h 時間軸 scrubber（REF lsappv3 sd_timeline + hls_timebar）：段色塊 + 事件標記 + 拖曳 seek */}
      {!isSd && loadState === 'ready' && clips.length > 0 && (
        <TimelineStrip
          range={win}
          clips={clips}
          events={eventsInDay}
          playheadUs={playheadUs}
          onSeek={seekToUs}
        />
      )}

      {!isSd && loadState === 'loading' && <p className="text-secondary">{t('playback.loadingClips')}</p>}

      {!isSd && loadState === 'error' && (
        <div className="card" role="alert" style={{ maxWidth: 560 }}>
          <p className="error-text" style={{ marginTop: 0 }}>
            {t('playback.loadClipsFailed', { error: loadError })}
          </p>
          <button className="btn-ghost" onClick={() => setReloadKey((k) => k + 1)}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {!isSd && loadState === 'ready' && clips.length === 0 && (
        <div className="card" style={{ maxWidth: 560, textAlign: 'center' }}>
          <Illustration name="no-recordings" alt="" size={160} />
          <p style={{ marginTop: 0 }}>{t('empty.noRecordings')}</p>
          <p className="text-secondary" style={{ marginBottom: 'var(--spacing-4)' }}>
            {t('playback.emptyTryAnotherDate')}
          </p>
          <button className="btn-ghost" data-testid="pb-jump-latest" disabled={jumping} onClick={() => void jumpToLatest()}>
            {jumping ? t('playback.searching') : t('playback.jumpToLatest')}
          </button>
        </div>
      )}

      {!isSd && loadState === 'ready' && clips.length > 0 && (
        <ul className="pb-cliplist" aria-label={t('playback.clipListLabel')}>
          {rows.map((row) =>
            row.kind === 'gap' ? (
              <li key={row.key} className="pb-gap text-secondary" aria-hidden>
                {t('playback.gapNoRecording', { duration: formatDuration(row.sec) })}
              </li>
            ) : (
              (() => {
                const clip = clips[row.idx];
                if (!clip) return null;
                const evCount = eventCountByClip.get(clip.clip_id) ?? 0;
                const active = currentIdx === row.idx;
                return (
                  <li key={clip.clip_id}>
                    <button className={`pb-clip ${active ? 'active' : ''}`} onClick={() => void playClip(row.idx)}>
                      <span className="pb-clip-time">
                        {timeOnly(clip.clip_start_ts, zone)} – {timeOnly(clip.clip_end_ts, zone)}
                      </span>
                      <span className="pb-clip-meta text-secondary">
                        {formatDuration((clip.clip_end_ts - clip.clip_start_ts) / 1_000_000)}
                        {clip.quality ? ` · ${clip.quality}` : ''}
                      </span>
                      <span className="pb-clip-badges">
                        {clip.has_event && <span className="pb-badge pb-badge-event">{t('playback.eventBadge')}{evCount > 0 ? ` ×${evCount}` : ''}</span>}
                        {active && <span className="pb-badge pb-badge-playing">{t('playback.playingBadge')}</span>}
                      </span>
                    </button>
                  </li>
                );
              })()
            )
          )}
          {/* Load-older sentinel (spec 17 §2.2): the IntersectionObserver above watches this
              row; while older ranges remain it shows a spinner, else a quiet end-of-list note. */}
          {!reachedStart ? (
            <li ref={bottomSentinelRef} className="pb-cliplist-more text-secondary" aria-hidden={!loadingMore}>
              {loadingMore ? t('playback.loadingClips') : ''}
            </li>
          ) : (
            clips.length >= 100 && (
              <li className="pb-cliplist-end text-secondary" aria-hidden>
                {t('playback.reachedListStart')}
              </li>
            )
          )}
        </ul>
      )}
    </section>
  );
}
